/**
 * Meshtastic Protocol Encoding/Decoding
 *
 * Handles conversion between CrowdTalkie messages and Meshtastic packets.
 * Uses a simplified binary format compatible with Meshtastic protobufs.
 *
 * ⚠️ EXPERIMENTAL: This integration is under active development.
 */

import {
  PORTNUM,
  PRIORITY,
  BROADCAST_ADDR,
  DEFAULT_HOP_LIMIT,
  CROWDTALKIE_APP_ID,
  CROWDTALKIE_MESSAGE_TYPE,
  MAX_TEXT_MESSAGE_LENGTH,
  MAX_LORA_PAYLOAD_SIZE,
} from './constants'
import type {
  MeshPacket,
  Data,
  FromRadio,
  CrowdTalkieMeshtasticMessage,
  MyNodeInfo,
  NodeInfo,
  Channel,
} from './types'

// ============= Protobuf-like Encoding Helpers =============

/**
 * Simple varint encoder (protobuf style)
 */
function encodeVarint(value: number): Uint8Array {
  const bytes: number[] = []
  while (value > 127) {
    bytes.push((value & 0x7f) | 0x80)
    value >>>= 7
  }
  bytes.push(value)
  return new Uint8Array(bytes)
}

/**
 * Simple varint decoder
 */
function decodeVarint(data: Uint8Array, offset: number): { value: number; bytesRead: number } {
  let value = 0
  let shift = 0
  let bytesRead = 0

  while (offset + bytesRead < data.length) {
    const byte = data[offset + bytesRead]!
    value |= (byte & 0x7f) << shift
    bytesRead++
    if ((byte & 0x80) === 0) break
    shift += 7
  }

  return { value, bytesRead }
}

/**
 * Encode a length-delimited field (string or bytes)
 */
function encodeLengthDelimited(fieldNumber: number, data: Uint8Array): Uint8Array {
  const tag = (fieldNumber << 3) | 2 // Wire type 2 = length-delimited
  const tagBytes = encodeVarint(tag)
  const lengthBytes = encodeVarint(data.length)

  const result = new Uint8Array(tagBytes.length + lengthBytes.length + data.length)
  result.set(tagBytes, 0)
  result.set(lengthBytes, tagBytes.length)
  result.set(data, tagBytes.length + lengthBytes.length)

  return result
}

/**
 * Encode a varint field
 */
function encodeVarintField(fieldNumber: number, value: number): Uint8Array {
  const tag = (fieldNumber << 3) | 0 // Wire type 0 = varint
  const tagBytes = encodeVarint(tag)
  const valueBytes = encodeVarint(value)

  const result = new Uint8Array(tagBytes.length + valueBytes.length)
  result.set(tagBytes, 0)
  result.set(valueBytes, tagBytes.length)

  return result
}

/**
 * Encode a fixed32 field
 */
function encodeFixed32Field(fieldNumber: number, value: number): Uint8Array {
  const tag = (fieldNumber << 3) | 5 // Wire type 5 = fixed32
  const tagBytes = encodeVarint(tag)

  const result = new Uint8Array(tagBytes.length + 4)
  result.set(tagBytes, 0)
  // Little endian
  result[tagBytes.length] = value & 0xff
  result[tagBytes.length + 1] = (value >>> 8) & 0xff
  result[tagBytes.length + 2] = (value >>> 16) & 0xff
  result[tagBytes.length + 3] = (value >>> 24) & 0xff

  return result
}

/**
 * Concatenate multiple Uint8Arrays
 */
function concatBytes(...arrays: Uint8Array[]): Uint8Array {
  const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0)
  const result = new Uint8Array(totalLength)
  let offset = 0
  for (const arr of arrays) {
    result.set(arr, offset)
    offset += arr.length
  }
  return result
}

// ============= Packet Encoding =============

/**
 * Encode a text message for Meshtastic
 */
export function encodeTextMessage(
  text: string,
  toNode: number = BROADCAST_ADDR,
  channelIndex: number = 0
): Uint8Array {
  // Truncate if too long
  const truncatedText = text.slice(0, MAX_TEXT_MESSAGE_LENGTH)
  const textBytes = new TextEncoder().encode(truncatedText)

  // Build Data message
  const dataFields = concatBytes(
    encodeVarintField(1, PORTNUM.TEXT_MESSAGE_APP), // portnum
    encodeLengthDelimited(2, textBytes), // payload
  )

  // Build MeshPacket
  const packetFields = concatBytes(
    encodeFixed32Field(2, toNode), // to
    encodeVarintField(4, channelIndex), // channel
    encodeLengthDelimited(5, dataFields), // decoded (Data)
    encodeVarintField(9, DEFAULT_HOP_LIMIT), // hop_limit
    encodeVarintField(11, PRIORITY.DEFAULT), // priority
  )

  // Build ToRadio with packet
  const toRadio = encodeLengthDelimited(1, packetFields)

  return toRadio
}

/**
 * Encode a CrowdTalkie message for Meshtastic PRIVATE_APP
 */
export function encodeCrowdTalkieMessage(
  message: CrowdTalkieMeshtasticMessage,
  toNode: number = BROADCAST_ADDR,
  channelIndex: number = 0
): Uint8Array {
  // Build CrowdTalkie payload
  // Format: [4-byte app ID][1-byte type][JSON payload]
  const appIdBytes = new TextEncoder().encode(CROWDTALKIE_APP_ID)
  const typeBytes = new Uint8Array([
    message.type === 'text' ? CROWDTALKIE_MESSAGE_TYPE.TEXT :
    message.type === 'state_sync' ? CROWDTALKIE_MESSAGE_TYPE.STATE_SYNC :
    message.type === 'poi' ? CROWDTALKIE_MESSAGE_TYPE.POI :
    CROWDTALKIE_MESSAGE_TYPE.PING
  ])
  const jsonBytes = new TextEncoder().encode(JSON.stringify({
    r: message.roomId,
    p: message.payload,
    s: message.signature,
    i: message.senderId,
    t: message.timestamp,
  }))

  const payload = concatBytes(appIdBytes, typeBytes, jsonBytes)

  // Check payload size
  if (payload.length > MAX_LORA_PAYLOAD_SIZE) {
    console.warn('[Meshtastic] Payload too large, will be fragmented:', payload.length)
  }

  // Build Data message
  const dataFields = concatBytes(
    encodeVarintField(1, PORTNUM.PRIVATE_APP), // portnum
    encodeLengthDelimited(2, payload), // payload
  )

  // Build MeshPacket
  const packetFields = concatBytes(
    encodeFixed32Field(2, toNode), // to
    encodeVarintField(4, channelIndex), // channel
    encodeLengthDelimited(5, dataFields), // decoded (Data)
    encodeVarintField(9, DEFAULT_HOP_LIMIT), // hop_limit
    encodeVarintField(11, PRIORITY.DEFAULT), // priority
  )

  // Build ToRadio with packet
  const toRadio = encodeLengthDelimited(1, packetFields)

  return toRadio
}

/**
 * Encode a ToRadio request for configuration
 */
export function encodeWantConfig(configId: number): Uint8Array {
  // ToRadio field 3 = want_config_id
  return encodeVarintField(3, configId)
}

/**
 * Encode a heartbeat message
 */
export function encodeHeartbeat(): Uint8Array {
  // ToRadio field 6 = heartbeat (empty message)
  const emptyMessage = new Uint8Array(0)
  return encodeLengthDelimited(6, emptyMessage)
}

// ============= Packet Decoding =============

interface DecodedField {
  fieldNumber: number
  wireType: number
  value: number | Uint8Array
}

/**
 * Decode protobuf fields from a buffer
 */
function decodeFields(data: Uint8Array): DecodedField[] {
  const fields: DecodedField[] = []
  let offset = 0

  while (offset < data.length) {
    const { value: tag, bytesRead: tagBytes } = decodeVarint(data, offset)
    offset += tagBytes

    const fieldNumber = tag >>> 3
    const wireType = tag & 0x07

    if (wireType === 0) {
      // Varint
      const { value, bytesRead } = decodeVarint(data, offset)
      offset += bytesRead
      fields.push({ fieldNumber, wireType, value })
    } else if (wireType === 2) {
      // Length-delimited
      const { value: length, bytesRead } = decodeVarint(data, offset)
      offset += bytesRead
      const value = data.slice(offset, offset + length)
      offset += length
      fields.push({ fieldNumber, wireType, value })
    } else if (wireType === 5) {
      // Fixed32
      const value =
        (data[offset] ?? 0) |
        ((data[offset + 1] ?? 0) << 8) |
        ((data[offset + 2] ?? 0) << 16) |
        ((data[offset + 3] ?? 0) << 24)
      offset += 4
      fields.push({ fieldNumber, wireType, value: value >>> 0 }) // Unsigned
    } else if (wireType === 1) {
      // Fixed64 - skip 8 bytes
      offset += 8
    } else {
      // Unknown wire type, try to skip
      console.warn('[Meshtastic] Unknown wire type:', wireType)
      break
    }
  }

  return fields
}

/**
 * Decode a FromRadio message
 */
export function decodeFromRadio(data: Uint8Array): FromRadio | null {
  try {
    const fields = decodeFields(data)
    const result: FromRadio = { id: 0 }

    for (const field of fields) {
      switch (field.fieldNumber) {
        case 1: // id
          result.id = field.value as number
          break
        case 2: // packet
          if (field.value instanceof Uint8Array) {
            result.packet = decodeMeshPacket(field.value)
          }
          break
        case 3: // my_info
          if (field.value instanceof Uint8Array) {
            result.myInfo = decodeMyNodeInfo(field.value)
          }
          break
        case 4: // node_info
          if (field.value instanceof Uint8Array) {
            result.nodeInfo = decodeNodeInfo(field.value)
          }
          break
        case 7: // config_complete_id
          result.configCompleteId = field.value as number
          break
        case 8: // rebooted
          result.rebooted = (field.value as number) !== 0
          break
        case 10: // channel
          if (field.value instanceof Uint8Array) {
            result.channel = decodeChannel(field.value)
          }
          break
      }
    }

    return result
  } catch (error) {
    console.error('[Meshtastic] Failed to decode FromRadio:', error)
    return null
  }
}

/**
 * Decode a MeshPacket
 */
function decodeMeshPacket(data: Uint8Array): MeshPacket {
  const fields = decodeFields(data)
  const packet: MeshPacket = {
    from: 0,
    to: 0,
    channel: 0,
    id: 0,
    rxTime: 0,
    rxSnr: 0,
    rxRssi: 0,
    hopLimit: 0,
    hopStart: 0,
    wantAck: false,
    priority: 'DEFAULT',
    viaMqtt: false,
    delayed: 'NO_DELAY',
  }

  for (const field of fields) {
    switch (field.fieldNumber) {
      case 1: // from
        packet.from = field.value as number
        break
      case 2: // to
        packet.to = field.value as number
        break
      case 4: // channel
        packet.channel = field.value as number
        break
      case 5: // decoded
        if (field.value instanceof Uint8Array) {
          packet.decoded = decodeData(field.value)
        }
        break
      case 6: // encrypted
        if (field.value instanceof Uint8Array) {
          packet.encrypted = field.value
        }
        break
      case 7: // id
        packet.id = field.value as number
        break
      case 8: // rx_time
        packet.rxTime = field.value as number
        break
      case 9: // hop_limit
        packet.hopLimit = field.value as number
        break
      case 10: // want_ack
        packet.wantAck = (field.value as number) !== 0
        break
    }
  }

  return packet
}

/**
 * Decode Data payload
 */
function decodeData(data: Uint8Array): Data {
  const fields = decodeFields(data)
  const result: Data = {
    portnum: 'UNKNOWN_APP',
    payload: new Uint8Array(0),
    wantResponse: false,
    dest: 0,
    source: 0,
    requestId: 0,
    replyId: 0,
    emoji: 0,
  }

  for (const field of fields) {
    switch (field.fieldNumber) {
      case 1: // portnum
        result.portnum = portnumToString(field.value as number)
        break
      case 2: // payload
        if (field.value instanceof Uint8Array) {
          result.payload = field.value
        }
        break
      case 3: // want_response
        result.wantResponse = (field.value as number) !== 0
        break
      case 4: // dest
        result.dest = field.value as number
        break
      case 5: // source
        result.source = field.value as number
        break
      case 6: // request_id
        result.requestId = field.value as number
        break
      case 7: // reply_id
        result.replyId = field.value as number
        break
      case 8: // emoji
        result.emoji = field.value as number
        break
    }
  }

  return result
}

/**
 * Decode MyNodeInfo
 */
function decodeMyNodeInfo(data: Uint8Array): MyNodeInfo {
  const fields = decodeFields(data)
  const result: MyNodeInfo = {
    myNodeNum: 0,
    hasGps: false,
    maxChannels: 0,
    firmwareVersion: '',
    rebootCount: 0,
    bitrate: 0,
    messageTimeoutMsec: 0,
    minAppVersion: 0,
    airPeriodTx: [],
    airPeriodRx: [],
  }

  for (const field of fields) {
    switch (field.fieldNumber) {
      case 1: // my_node_num
        result.myNodeNum = field.value as number
        break
      case 3: // has_gps
        result.hasGps = (field.value as number) !== 0
        break
      case 6: // max_channels
        result.maxChannels = field.value as number
        break
      case 7: // firmware_version
        if (field.value instanceof Uint8Array) {
          result.firmwareVersion = new TextDecoder().decode(field.value)
        }
        break
      case 8: // reboot_count
        result.rebootCount = field.value as number
        break
      case 9: // bitrate
        result.bitrate = field.value as number
        break
    }
  }

  return result
}

/**
 * Decode NodeInfo
 */
function decodeNodeInfo(data: Uint8Array): NodeInfo {
  const fields = decodeFields(data)
  const result: NodeInfo = {
    num: 0,
    snr: 0,
    lastHeard: 0,
    channel: 0,
    viaMqtt: false,
    hopsAway: 0,
  }

  for (const field of fields) {
    switch (field.fieldNumber) {
      case 1: // num
        result.num = field.value as number
        break
      case 7: // snr
        result.snr = field.value as number
        break
      case 8: // last_heard
        result.lastHeard = field.value as number
        break
      case 9: // channel
        result.channel = field.value as number
        break
    }
  }

  return result
}

/**
 * Decode Channel
 */
function decodeChannel(data: Uint8Array): Channel {
  const fields = decodeFields(data)
  const result: Channel = {
    index: 0,
    settings: {
      channelNum: 0,
      psk: new Uint8Array(0),
      name: '',
      id: 0,
      uplinkEnabled: false,
      downlinkEnabled: false,
    },
    role: 'DISABLED',
  }

  for (const field of fields) {
    switch (field.fieldNumber) {
      case 1: // index
        result.index = field.value as number
        break
      case 2: // settings
        if (field.value instanceof Uint8Array) {
          const settingsFields = decodeFields(field.value)
          for (const sf of settingsFields) {
            switch (sf.fieldNumber) {
              case 1: // channel_num
                result.settings.channelNum = sf.value as number
                break
              case 2: // psk
                if (sf.value instanceof Uint8Array) {
                  result.settings.psk = sf.value
                }
                break
              case 3: // name
                if (sf.value instanceof Uint8Array) {
                  result.settings.name = new TextDecoder().decode(sf.value)
                }
                break
            }
          }
        }
        break
      case 3: // role
        result.role = channelRoleToString(field.value as number)
        break
    }
  }

  return result
}

/**
 * Convert portnum number to string
 */
function portnumToString(portnum: number): Data['portnum'] {
  const portMap: Record<number, Data['portnum']> = {
    [PORTNUM.UNKNOWN_APP]: 'UNKNOWN_APP',
    [PORTNUM.TEXT_MESSAGE_APP]: 'TEXT_MESSAGE_APP',
    [PORTNUM.POSITION_APP]: 'POSITION_APP',
    [PORTNUM.NODEINFO_APP]: 'NODEINFO_APP',
    [PORTNUM.ROUTING_APP]: 'ROUTING_APP',
    [PORTNUM.ADMIN_APP]: 'ADMIN_APP',
    [PORTNUM.PRIVATE_APP]: 'PRIVATE_APP',
    [PORTNUM.TELEMETRY_APP]: 'TELEMETRY_APP',
  }
  return portMap[portnum] ?? 'UNKNOWN_APP'
}

/**
 * Convert channel role number to string
 */
function channelRoleToString(role: number): Channel['role'] {
  const roleMap: Record<number, Channel['role']> = {
    0: 'DISABLED',
    1: 'PRIMARY',
    2: 'SECONDARY',
  }
  return roleMap[role] ?? 'DISABLED'
}

// ============= CrowdTalkie Message Parsing =============

/**
 * Parse a CrowdTalkie message from Meshtastic PRIVATE_APP payload
 */
export function parseCrowdTalkiePayload(
  payload: Uint8Array
): CrowdTalkieMeshtasticMessage | null {
  try {
    // Check app ID prefix
    const appId = new TextDecoder().decode(payload.slice(0, 4))
    if (appId !== CROWDTALKIE_APP_ID) {
      return null // Not a CrowdTalkie message
    }

    const typeByte = payload[4]
    const jsonBytes = payload.slice(5)
    const jsonStr = new TextDecoder().decode(jsonBytes)
    const data = JSON.parse(jsonStr)

    const type =
      typeByte === CROWDTALKIE_MESSAGE_TYPE.TEXT ? 'text' :
      typeByte === CROWDTALKIE_MESSAGE_TYPE.STATE_SYNC ? 'state_sync' :
      typeByte === CROWDTALKIE_MESSAGE_TYPE.POI ? 'poi' :
      'ping'

    return {
      roomId: data.r,
      type,
      payload: data.p,
      signature: data.s,
      senderId: data.i,
      timestamp: data.t,
    }
  } catch (error) {
    console.error('[Meshtastic] Failed to parse CrowdTalkie payload:', error)
    return null
  }
}

/**
 * Parse a text message from Meshtastic TEXT_MESSAGE_APP payload
 */
export function parseTextPayload(payload: Uint8Array): string {
  return new TextDecoder().decode(payload)
}

// ============= Utility Functions =============

/**
 * Generate a unique packet ID
 */
export function generatePacketId(): number {
  return Math.floor(Math.random() * 0xffffffff)
}

/**
 * Convert node number to hex string for display
 */
export function nodeNumToHex(nodeNum: number): string {
  return '!' + nodeNum.toString(16).padStart(8, '0')
}

/**
 * Convert hex string to node number
 */
export function hexToNodeNum(hex: string): number {
  return parseInt(hex.replace('!', ''), 16)
}

/**
 * Check if a packet is a broadcast
 */
export function isBroadcast(toNode: number): boolean {
  return toNode === BROADCAST_ADDR
}

/**
 * Calculate approximate message delivery time based on modem preset
 * Returns time in milliseconds
 */
export function estimateDeliveryTime(payloadSize: number, modemPreset: number): number {
  // Approximate airtime calculations based on Meshtastic documentation
  const presetTimes: Record<number, number> = {
    0: 1000,   // LONG_FAST: ~1 second
    1: 5000,   // LONG_SLOW: ~5 seconds
    2: 10000,  // VERY_LONG_SLOW: ~10 seconds
    3: 2000,   // MEDIUM_SLOW: ~2 seconds
    4: 500,    // MEDIUM_FAST: ~500ms
    5: 1000,   // SHORT_SLOW: ~1 second
    6: 200,    // SHORT_FAST: ~200ms
    7: 2000,   // LONG_MODERATE: ~2 seconds
    8: 100,    // SHORT_TURBO: ~100ms
  }

  const baseTime = presetTimes[modemPreset] ?? 1000
  // Adjust for payload size (rough approximation)
  return baseTime * (1 + payloadSize / 256)
}
